import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { Pointer, Redo2, Undo2 } from 'lucide-react';
import PracticeSettingsService from '../services/PracticeSettingsService';
import { getCardMessageOptions, getCardMessageLabel } from '../services/CardMessageService';
import { getGraduationStamp } from '../utils/graduationStamp';
import MarkdownText from './MarkdownText';
import PointBonusTip, { POINT_BONUS_FEEDBACK_MS as SCORE_BONUS_FEEDBACK_MS } from './PointBonusTip';
import CardJudgmentFeedback, { JUDGMENT_FEEDBACK_MS } from './CardJudgmentFeedback';
import { stripSimpleMarkdown } from '../utils/simpleMarkdown';
import { t } from '../i18n';
import useCardSwipeGesture from '../hooks/useCardSwipeGesture';
import './FlashCard.css';

const AUTO_REPEAT_IDLE_HINT_MS = 10000;
const ENCOUNTER_FLIP_MS = 360;

/**
 * FlashCard component displays a flash card with flip functionality and swipe gestures
 *
 * @param {Object} props
 * @param {Object} props.card - Flash card object with front, back, practiceCount
 * @param {string} props.card.front - Front side content
 * @param {string} props.card.back - Back side content
 * @param {number} props.card.ordinal - Optional one-based book position, shown only on vocabulary cards
 * @param {number} props.card.grade - Vocabulary mastery grade at session start (0–5)
 * @param {number} props.card.practiceCount - Number of times practiced (total)
 * @param {number} props.sessionScoreGained - Score gained for this card in this session
 * @param {Array<{label:string,points:number}>} props.scoreBonuses - Latest score bonuses earned
 * @param {number} props.scoreBonusEventId - Changes whenever a repeat increase recalculates bonuses
 * @param {number} props.sessionPracticeCount - Number of times practiced in this session
 * @param {boolean} props.isFlipped - Whether the card is flipped (optional, controlled mode)
 * @param {Function} props.onFlip - Callback when card is clicked to practice
 * @param {Function} props.onSwipeUp - Callback for swipe up gesture (Next)
 * @param {Function} props.onSwipeDown - Callback for swipe down gesture (Previous)
 * @param {boolean} props.verticalNavigationDisabled - Whether vertical next/previous controls are disabled
 * @param {boolean} props.hideVerticalNavigation - Whether vertical next/previous controls should be hidden
 * @param {boolean} props.hidePreviousNavigation - Whether backward navigation is unavailable in this drill
 * @param {boolean} props.hideNextNavigation - Disable manual next navigation through button, swipe and keyboard
 * @param {boolean} props.showBeginnerArrowLabels - Whether beginner gesture labels and practice captions should be shown
 * @param {'recall'|'repeat'|null} props.practiceGuidancePhase - Ordinary practice's caption stage
 * @param {boolean} props.showPersistentGuidance - Keep beginner control captions visible after interactions
 * @param {boolean} props.showPracticePhaseCaption - Use ordinary phase captions and guidance instead of fluency's face labels
 * @param {boolean} props.vocabularyAnswerRevealed - Whether this vocabulary card's answer has been exposed in this session
 * @param {boolean} props.practiceGuidanceEnabled - Suspend guidance while the parent is interrupted
 * @param {Function} props.onSwipeLeft - Legacy callback for swipe left gesture
 * @param {Function} props.onSwipeRight - Legacy callback for swipe right gesture
 * @param {Function} props.onReveal - Callback for horizontal reveal/hide gesture
 * @param {Function} props.onRevealComplete - Callback after the horizontal flip animation finishes
 * @param {Function} props.onAnswerViewed - Callback when answer side becomes visible
 * @param {Function} props.onAnswerOk - Callback when O is chosen in Self Test
 * @param {Function} props.onAnswerNo - Callback when X is chosen in Self Test
 * @param {boolean} props.answerChoiceAvailable - Whether the one-shot Self Test decision is available
 * @param {Function} props.onBadgeClick - Callback when session count badge is clicked
 * @param {Function} props.onAutoRepeat - Callback when score badge is long-pressed
 * @param {boolean} props.autoRepeatAvailable - Whether score badge long-press can auto-repeat
 * @param {number} props.autoRepeatHoldMs - Long-press duration for auto-repeat
 * @param {boolean} props.reverseMode - If true, show back first then front
 * @param {boolean} props.isCompleted - Whether to show the workout cleared stamp
 * @param {string} props.noteCoverUrl - Optional note cover image URL to show faintly on the card
 * @param {{visible:boolean,status:string,label:string,value:string}} props.quickTestStatus - Grade 4 quick-test caption state
 * @param {boolean} props.keyboardShortcutsEnabled - Whether global practice keyboard shortcuts are active
 * @param {'practice'|'encounter'|'vocabulary'} props.interactionMode - Vocabulary uses directional flips and next/previous navigation without self-assessment
 * @param {{id:string,direction:'left'|'right'}} props.revealRequest - One-shot request to reveal the answer, such as after timeout
 * @param {boolean} props.answerOkDisabled - Whether the positive answer choice is unavailable
 */
function FlashCard({
  card,
  isFlipped: controlledIsFlipped,
  onFlip,
  sessionScoreGained = 0,
  scoreBonuses = [],
  scoreBonusEventId = 0,
  sessionPracticeCount = 0,
  onSwipeUp,
  onSwipeDown,
  onSwipeLeft,
  onSwipeRight,
  onReveal,
  onRevealComplete,
  onAnswerViewed,
  onAnswerOk,
  onAnswerNo,
  answerChoiceAvailable = true,
  answerOkDisabled = false,
  interactionMode = 'practice',
  revealRequest = null,
  displayedSide = null,
  onDisplayedSideChange,
  onBadgeClick,
  onAutoRepeat,
  autoRepeatAvailable: requestedAutoRepeatAvailable = false,
  autoRepeatHoldMs = 1000,
  reverseMode = false,
  isCompleted = false,
  noteCoverUrl = '',
  quickTestStatus = null,
  hintMessages = [],
  showHintMessages: requestedShowHintMessages = false,
  hintMessageSeed = 0,
  messageButtonAvailable: requestedMessageButtonAvailable = false,
  onSaveHintMessages,
  onClearMessageButton,
  messageOptions = null,
  verticalNavigationDisabled: requestedVerticalNavigationDisabled = false,
  hideVerticalNavigation: requestedHideVerticalNavigation = false,
  hidePreviousNavigation = false,
  hideNextNavigation = false,
  showBeginnerArrowLabels: requestedShowBeginnerArrowLabels = false,
  practiceGuidancePhase = null,
  showPersistentGuidance = false,
  showPracticePhaseCaption = true,
  vocabularyAnswerRevealed = false,
  practiceGuidanceEnabled = true,
  interactionDisabled = false,
  keyboardShortcutsEnabled = false,
  backPracticeHint = 'Click to practice',
}) {
  const isEncounterMode = interactionMode === 'encounter';
  const isVocabularyMode = interactionMode === 'vocabulary';
  const isRecallMode = isEncounterMode || isVocabularyMode;
  const autoRepeatAvailable = requestedAutoRepeatAvailable && !isRecallMode;
  const showHintMessages = requestedShowHintMessages && !isRecallMode;
  const messageButtonAvailable = requestedMessageButtonAvailable && !isRecallMode;
  const verticalNavigationDisabled = requestedVerticalNavigationDisabled || isEncounterMode;
  const hideVerticalNavigation = requestedHideVerticalNavigation || isEncounterMode;
  const showBeginnerArrowLabels = requestedShowBeginnerArrowLabels && !isEncounterMode;
  const cardIdentity = card?.id ?? card?._id;
  const [visibleBonusEvent, setVisibleBonusEvent] = useState(null);
  const hasScoreBonuses = scoreBonuses.length > 0;
  useEffect(() => {
    if (!hasScoreBonuses) {
      setVisibleBonusEvent(null);
      return undefined;
    }
    setVisibleBonusEvent({ cardIdentity, eventId: scoreBonusEventId });
    const timer = setTimeout(() => setVisibleBonusEvent(null), SCORE_BONUS_FEEDBACK_MS);
    return () => clearTimeout(timer);
  }, [cardIdentity, scoreBonusEventId, hasScoreBonuses]);
  const [displaySide, setDisplaySide] = useState('front');
  const [flipState, setFlipState] = useState(null);
  const flipInFlightRef = useRef(null);
  const encounterFlipTimerRef = useRef(null);
  const handledRevealRequestsRef = useRef(new Set());
  const encounterDecisionMadeRef = useRef(false);
  const [encounterDecisionMade, setEncounterDecisionMade] = useState(false);
  const flipCompletionHandlersRef = useRef(null);
  const gestureResetRef = useRef(null);
  flipCompletionHandlersRef.current = { onRevealComplete, onAnswerViewed, onDisplayedSideChange };
  const rotationRef = useRef(0);
  const prevCountRef = useRef(sessionPracticeCount);
  const autoRepeatTimerRef = useRef(null);
  const autoRepeatIdleHintTimerRef = useRef(null);
  const autoRepeatPointerStartRef = useRef(null);
  const suppressBadgeClickRef = useRef(false);
  const [isFlashing, setIsFlashing] = useState(false);
  const [isAutoRepeatCharging, setIsAutoRepeatCharging] = useState(false);
  const [isAutoRepeatBursting, setIsAutoRepeatBursting] = useState(false);
  const [isAutoRepeatIdleHintVisible, setIsAutoRepeatIdleHintVisible] = useState(false);
  const [isGuidancePageActive, setIsGuidancePageActive] = useState(() => !document.hidden);
  const [isMessageChooserOpen, setIsMessageChooserOpen] = useState(false);
  const [messageSelection, setMessageSelection] = useState([]);
  const [selfTestFeedback, setSelfTestFeedback] = useState(null);
  const activeSide = flipState?.to || displaySide;
  const isAnswerSide = activeSide === 'back';
  const showsAnswerDecisionControls = !isVocabularyMode && isAnswerSide && answerChoiceAvailable;
  const guidancePhase = !isRecallMode && ['recall', 'repeat'].includes(practiceGuidancePhase)
    ? (showsAnswerDecisionControls ? 'answer' : practiceGuidancePhase) : null;
  const idleHintAvailable = (autoRepeatAvailable || Boolean(guidancePhase)) && practiceGuidanceEnabled
    && !interactionDisabled && !flipState && !isMessageChooserOpen
    && (!showsAnswerDecisionControls || guidancePhase === 'answer');
  const availableMessageOptions = useMemo(
    () => Array.isArray(messageOptions) ? messageOptions.slice(0, 20) : getCardMessageOptions(card),
    [card, messageOptions]
  );

  const clearAutoRepeatIdleHintTimer = useCallback(() => {
    if (autoRepeatIdleHintTimerRef.current) {
      clearTimeout(autoRepeatIdleHintTimerRef.current);
      autoRepeatIdleHintTimerRef.current = null;
    }
  }, []);

  const restartAutoRepeatIdleHint = useCallback(() => {
    clearAutoRepeatIdleHintTimer();
    setIsAutoRepeatIdleHintVisible(false);

    if (!idleHintAvailable || document.hidden) {
      return;
    }

    autoRepeatIdleHintTimerRef.current = setTimeout(() => {
      autoRepeatIdleHintTimerRef.current = null;
      setIsAutoRepeatIdleHintVisible(true);
    }, AUTO_REPEAT_IDLE_HINT_MS);
  }, [idleHintAvailable, clearAutoRepeatIdleHintTimer]);

  useEffect(() => {
    if (sessionPracticeCount > prevCountRef.current) {
      setIsFlashing(false);
      requestAnimationFrame(() => {
        setIsFlashing(true);
      });
    }
    prevCountRef.current = sessionPracticeCount;
  }, [sessionPracticeCount]);

  useEffect(() => {
    flipInFlightRef.current = null;
    clearTimeout(encounterFlipTimerRef.current);
    encounterFlipTimerRef.current = null;
    handledRevealRequestsRef.current.clear();
    encounterDecisionMadeRef.current = false;
    setEncounterDecisionMade(false);
    rotationRef.current = 0;
    setDisplaySide('front');
    setFlipState(null);
    setIsMessageChooserOpen(false);
    setMessageSelection([]);
    setSelfTestFeedback(null);
  }, [cardIdentity]);

  // A resumed vocabulary appearance restores its physical face independently of
  // the sticky server-side "answer seen" state, without recording another reveal.
  useEffect(() => {
    if (!isVocabularyMode || flipInFlightRef.current || !['front', 'back'].includes(displayedSide) || displayedSide === displaySide) return;
    setDisplaySide(displayedSide);
    rotationRef.current = displayedSide === 'back' ? 180 : 0;
  }, [cardIdentity, displaySide, displayedSide, isVocabularyMode]);

  useEffect(() => {
    if (!selfTestFeedback) return undefined;
    const timeout = setTimeout(() => setSelfTestFeedback(null), JUDGMENT_FEEDBACK_MS);
    return () => clearTimeout(timeout);
  }, [selfTestFeedback]);

  useEffect(() => {
    if (!messageButtonAvailable) {
      setIsMessageChooserOpen(false);
      setMessageSelection([]);
    }
  }, [messageButtonAvailable]);

  useEffect(() => () => {
    clearTimeout(encounterFlipTimerRef.current);
    if (autoRepeatTimerRef.current) {
      clearTimeout(autoRepeatTimerRef.current);
      autoRepeatTimerRef.current = null;
    }
    clearAutoRepeatIdleHintTimer();
  }, [clearAutoRepeatIdleHintTimer]);

  useEffect(() => {
    restartAutoRepeatIdleHint();
  }, [restartAutoRepeatIdleHint, cardIdentity, sessionPracticeCount, displaySide, guidancePhase]);

  useEffect(() => {
    if (!idleHintAvailable && !practiceGuidancePhase) return undefined;
    const pause = () => {
      clearAutoRepeatIdleHintTimer();
      setIsAutoRepeatIdleHintVisible(false);
      setIsGuidancePageActive(false);
    };
    const resume = () => {
      setIsGuidancePageActive(!document.hidden);
      restartAutoRepeatIdleHint();
    };
    const visibility = () => document.hidden ? pause() : resume();
    const events = ['pointerdown', 'touchstart', 'click', 'keydown', 'wheel'];
    events.forEach(name => window.addEventListener(name, restartAutoRepeatIdleHint, true));
    window.addEventListener('blur', pause);
    window.addEventListener('focus', resume);
    document.addEventListener('visibilitychange', visibility);
    return () => {
      events.forEach(name => window.removeEventListener(name, restartAutoRepeatIdleHint, true));
      window.removeEventListener('blur', pause);
      window.removeEventListener('focus', resume);
      document.removeEventListener('visibilitychange', visibility);
    };
  }, [idleHintAvailable, practiceGuidancePhase, clearAutoRepeatIdleHintTimer, restartAutoRepeatIdleHint]);

  useEffect(() => {
    if (isAutoRepeatCharging || isAutoRepeatBursting || isFlashing) {
      setIsAutoRepeatIdleHintVisible(false);
    }
  }, [isAutoRepeatCharging, isAutoRepeatBursting, isFlashing]);

  useEffect(() => {
    if (!idleHintAvailable) {
      clearAutoRepeatIdleHintTimer();
      setIsAutoRepeatIdleHintVisible(false);
    }
  }, [idleHintAvailable, clearAutoRepeatIdleHintTimer]);

  const handleCardClick = (event) => {
    if (interactionDisabled || flipInFlightRef.current || swipe.isSettling()) return;
    if (event?.target?.closest?.('button')) return;
    if (isVocabularyMode) {
      handleVerticalArrowClick(event, 'next');
      return;
    }
    if (showsAnswerDecisionControls || isRecallMode) {
      if (!event) return;
      if (isEncounterMode && encounterDecisionMadeRef.current) return;
      const bounds = event.currentTarget.getBoundingClientRect();
      const direction = event.clientX < bounds.left + bounds.width / 2 ? 'left' : 'right';
      applyAnswerChoiceForDirection(direction);
      startHorizontalFlip(direction);
      return;
    }
    restartAutoRepeatIdleHint();
    if (onClearMessageButton) {
      onClearMessageButton();
    }
    if (onFlip) {
      onFlip();
    }
  };

  const startHorizontalFlip = (direction, gestureMotion = null) => {
    if (interactionDisabled || flipInFlightRef.current) return;
    if (isEncounterMode && (displaySide !== 'front' || encounterDecisionMadeRef.current)) return;
    swipe.cancel();

    const from = displaySide;
    const to = from === 'front' ? 'back' : 'front';
    const nextFlip = {
      direction,
      from,
      to,
      fromRotation: rotationRef.current + (gestureMotion?.rotation || 0),
      toRotation: rotationRef.current + (direction === 'left' ? -180 : 180),
      fromDragX: gestureMotion?.x || 0,
      durationMs: gestureMotion ? 240 : isRecallMode ? ENCOUNTER_FLIP_MS : 700,
    };
    flipInFlightRef.current = nextFlip;
    setFlipState(nextFlip);

    if (onReveal) {
      onReveal({ direction, from, to });
    }
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      completeHorizontalFlip();
    } else {
      // Complete even when a browser suppresses or drops animationend.
      encounterFlipTimerRef.current = setTimeout(completeHorizontalFlip, nextFlip.durationMs);
    }
  };

  useEffect(() => {
    if (!isEncounterMode || revealRequest?.id == null || interactionDisabled || flipInFlightRef.current) return;
    const requestId = revealRequest.id;
    if (handledRevealRequestsRef.current.has(requestId)) return;
    handledRevealRequestsRef.current.add(requestId);
    if (displaySide === 'front') {
      startHorizontalFlip(revealRequest.direction === 'left' ? 'left' : 'right');
    }
  });

  const applyAnswerChoiceForDirection = (direction) => {
    if (isVocabularyMode || interactionDisabled || flipInFlightRef.current || displaySide !== 'back' || !answerChoiceAvailable) {
      return;
    }
    if (direction === 'right' && answerOkDisabled) return;
    if (isEncounterMode) {
      if (encounterDecisionMadeRef.current) return;
      encounterDecisionMadeRef.current = true;
      setEncounterDecisionMade(true);
    }

    if (direction === 'right' && onAnswerOk) {
      onAnswerOk();
    } else if (direction === 'left' && onAnswerNo) {
      onAnswerNo();
    }
    if (!isEncounterMode) setSelfTestFeedback(direction === 'right' ? 'correct' : 'retry');
  };

  const handleArrowClick = (e, direction) => {
    e.stopPropagation();
    if (interactionDisabled || flipInFlightRef.current || swipe.isSettling()) return;

    if (direction === 'left' && messageButtonAvailable && !showsAnswerDecisionControls) {
      restartAutoRepeatIdleHint();
      setIsMessageChooserOpen(true);
      setMessageSelection(hintMessages.slice(0, 2));
      return;
    }

    if (onClearMessageButton) {
      onClearMessageButton();
    }
    applyAnswerChoiceForDirection(direction);
    startHorizontalFlip(direction);
  };

  const handleVerticalArrowClick = (e, navigation) => {
    e.stopPropagation();
    if (interactionDisabled || swipe.isSettling() || verticalNavigationDisabled || showsAnswerDecisionControls || ((isVocabularyMode || hidePreviousNavigation) && navigation === 'previous')) return;
    swipe.cancel();

    if (onClearMessageButton) {
      onClearMessageButton();
    }
    const handler = navigation === 'next'
      ? (onSwipeUp || onSwipeLeft)
      : (onSwipeDown || onSwipeRight);
    handler?.();
  };

  useEffect(() => {
    if (!keyboardShortcutsEnabled || interactionDisabled) return undefined;

    const handleKeyboardShortcut = (event) => {
      if (swipe.isSettling()) return;
      if (event.defaultPrevented || event.repeat || event.ctrlKey || event.metaKey || event.altKey) return;
      const key = String(event.key || '').toLowerCase();
      if (event.target?.closest?.('input, textarea, select, [contenteditable="true"]')) return;
      if (event.target?.closest?.('button') && (key === 'enter' || key === ' ')) return;
      const isLeft = ['a', 'arrowleft', '4'].includes(key) || event.code === 'Numpad4';
      const isRight = ['d', 'arrowright', '6'].includes(key) || event.code === 'Numpad6';
      const isNext = ['s', 'arrowdown', '2'].includes(key) || event.code === 'Numpad2';
      const isPrevious = ['w', 'arrowup', '8'].includes(key) || event.code === 'Numpad8';

      if (isLeft || isRight) {
        event.preventDefault();
        swipe.cancel();
        onClearMessageButton?.();
        const direction = isLeft ? 'left' : 'right';
        applyAnswerChoiceForDirection(direction);
        startHorizontalFlip(direction);
        return;
      }

      if (isNext || isPrevious) {
        if (hideNextNavigation && isNext) return;
        if ((isVocabularyMode || hidePreviousNavigation) && isPrevious) return;
        if (verticalNavigationDisabled || showsAnswerDecisionControls) return;
        swipe.cancel();
        const handler = isNext ? (onSwipeUp || onSwipeLeft) : (onSwipeDown || onSwipeRight);
        if (!handler) return;
        event.preventDefault();
        onClearMessageButton?.();
        handler();
        return;
      }

      if (key === 'escape') return;
      if (isRecallMode) return;
      if (key.length === 1 || key === 'enter' || key === ' ') {
        event.preventDefault();
        handleCardClick();
      }
    };

    window.addEventListener('keydown', handleKeyboardShortcut);
    return () => window.removeEventListener('keydown', handleKeyboardShortcut);
  });

  const completeHorizontalFlip = useCallback(() => {
    if (!flipInFlightRef.current) return;
    clearTimeout(encounterFlipTimerRef.current);
    encounterFlipTimerRef.current = null;
    const { direction, from, to, toRotation } = flipInFlightRef.current;
    const completedFlip = { direction, from, to };
    const nextSide = completedFlip.to;
    // Keep the exact final angle so removing the animation cannot spin it back.
    rotationRef.current = toRotation;
    flipInFlightRef.current = null;
    setDisplaySide(nextSide);
    setFlipState(null);
    gestureResetRef.current?.();
    flipCompletionHandlersRef.current.onDisplayedSideChange?.(nextSide);
    flipCompletionHandlersRef.current.onRevealComplete?.(completedFlip);

    if (nextSide === 'back') {
      flipCompletionHandlersRef.current.onAnswerViewed?.();
    }
  }, []);

  const handleFlipAnimationEnd = (e) => {
    if (e.target !== e.currentTarget || !flipInFlightRef.current) return;
    if (e.animationName && e.animationName !== 'flashcardHorizontalFlip') return;
    completeHorizontalFlip();
  };

  const getCountColorClass = (count) => {
    if (count === 0) return 'count-0';
    if (count <= 4) return 'count-1-4';
    if (count <= 9) return 'count-5-9';
    if (count <= 14) return 'count-10-14';
    if (count <= 19) return 'count-15-19';
    return 'count-20-plus';
  };

  const getFontSizeClass = (text) => {
    if (!text) return '';

    const plainText = stripSimpleMarkdown(text);

    const cjkRegex = /[\u3000-\u303f\u3040-\u309f\u30a0-\u30ff\uff00-\uff9f\u4e00-\u9faf\u3400-\u4dbf\uac00-\ud7a3]/g;
    const cjkMatches = plainText.match(cjkRegex) || [];
    const cjkCount = cjkMatches.length;
    const nonCjkCount = plainText.length - cjkCount;
    const visualWidth = nonCjkCount + (cjkCount * 1.5);

    return visualWidth > 60 ? 'small-font' : '';
  };

  const frontFaceText = reverseMode ? card.back : card.front;
  const backFaceText = reverseMode ? card.front : card.back;
  const frontFaceMemo = isVocabularyMode ? null : (reverseMode ? card.backMemo : card.memo);
  const backFaceMemo = isVocabularyMode ? card.backMemo : (reverseMode ? card.memo : card.backMemo);
  const vocabularyOrdinalBadge = isVocabularyMode && Number.isSafeInteger(card.ordinal) && card.ordinal > 0
    ? <span className="flashcard-card-number" aria-label={t('vocabulary.bookOrdinal', undefined, { current: card.ordinal })}>#{card.ordinal}</span>
    : null;
  const vocabularyGradeBadge = isVocabularyMode && Number.isInteger(card.grade) && card.grade >= 0 && card.grade <= 5
    ? <span className="flashcard-grade-badge" aria-label={t('vocabulary.wordGrade', undefined, { grade: `${card.grade} / 5` })}>GRADE {card.grade}/5</span>
    : null;
  const frontFontClass = getFontSizeClass(frontFaceText);
  const backFontClass = getFontSizeClass(backFaceText);
  const swipe = useCardSwipeGesture({
    identity: cardIdentity,
    disabled: interactionDisabled || Boolean(flipState),
    maxRotation: 75,
    canSwipe: direction => {
      if (flipInFlightRef.current) return false;
      if (direction === 'up' || direction === 'down') return !verticalNavigationDisabled && !showsAnswerDecisionControls &&
        Boolean(direction === 'up' ? !hideNextNavigation && (onSwipeUp || onSwipeLeft) : !isVocabularyMode && !hidePreviousNavigation && (onSwipeDown || onSwipeRight));
      if (isEncounterMode && (encounterDecisionMadeRef.current || (displaySide === 'back' && (!answerChoiceAvailable || (direction === 'right' && answerOkDisabled))))) return false;
      return true;
    },
    onHorizontal: (direction, departure) => {
      restartAutoRepeatIdleHint();
      onClearMessageButton?.();
      applyAnswerChoiceForDirection(direction);
      startHorizontalFlip(direction, departure);
      return Boolean(flipInFlightRef.current);
    },
    onVertical: direction => {
      restartAutoRepeatIdleHint();
      onClearMessageButton?.();
      return (direction === 'up' ? (onSwipeUp || onSwipeLeft) : (onSwipeDown || onSwipeRight))?.();
    },
  });
  gestureResetRef.current = swipe.reset;
  const showQuickTestCaption = !isRecallMode && Boolean(quickTestStatus?.visible);
  const quickTestCaptionClass = showQuickTestCaption
    ? `flashcard-corner-caption-quick-test is-${quickTestStatus.status || 'active'}`
    : '';
  const horizontalLeftLabel = showsAnswerDecisionControls
    ? (isEncounterMode ? `X, ${t('practiceUi.encounter.practiceAgain')}` : 'X')
    : (activeSide === 'back' ? 'Hide answer' : 'Reveal answer');
  const horizontalRightLabel = showsAnswerDecisionControls
    ? (isEncounterMode ? `O, ${t('practiceUi.encounter.remembered')}` : 'O')
    : (activeSide === 'back' ? 'Hide answer' : 'Reveal answer');
  const showsMessageButton = messageButtonAvailable && !showsAnswerDecisionControls;
  const showClearedStamp = !isRecallMode && (isCompleted || PracticeSettingsService.isCardCompleted(card));
  const graduationType = PracticeSettingsService.getCardGraduationType(card) ||
    (showClearedStamp ? PracticeSettingsService.GRADUATION_TYPES.MANUAL : null);
  const cardLevel = PracticeSettingsService.getCardLevel(card);
  const cardType = PracticeSettingsService.getCardType(card);
  const levelBadgeLabel = !isRecallMode && PracticeSettingsService.getCardLevelBadgeLabel(card);
  const encounterControlsDisabled = isEncounterMode && (Boolean(flipState) || encounterDecisionMade);
  const levelClass = `flashcard-level-${cardLevel}`;
  const typeClass = cardType ? `flashcard-type-${cardType}` : '';
  const flipClass = flipState
    ? `is-flipping flip-${flipState.direction} flip-from-${flipState.from} flip-to-${flipState.to}`
    : '';
  const safeHintMessages = (Array.isArray(hintMessages) ? hintMessages : []).filter(Boolean).slice(0, 2);
  const shouldRenderHintMessages = showHintMessages && activeSide === 'front' && safeHintMessages.length > 0;
  const hintLayout = useMemo(() => {
    const seedText = `${card?.id || ''}:${hintMessageSeed}:${safeHintMessages.join('|')}`;
    let hash = 2166136261;
    for (let i = 0; i < seedText.length; i += 1) {
      hash ^= seedText.charCodeAt(i);
      hash = Math.imul(hash, 16777619);
    }
    const next = () => {
      hash = Math.imul(hash ^ (hash >>> 15), 2246822507);
      hash = Math.imul(hash ^ (hash >>> 13), 3266489909);
      hash = (hash ^ (hash >>> 16)) >>> 0;
      return hash / 4294967295;
    };
    const zones = [
      { topMin: 16, topMax: 28, leftMin: 16, leftMax: 34 },
      { topMin: 16, topMax: 28, leftMin: 66, leftMax: 84 },
      { topMin: 72, topMax: 86, leftMin: 14, leftMax: 32 },
      { topMin: 72, topMax: 86, leftMin: 68, leftMax: 86 },
      { topMin: 38, topMax: 52, leftMin: 6, leftMax: 16 },
      { topMin: 38, topMax: 52, leftMin: 84, leftMax: 94 },
    ];

    return safeHintMessages.map((message, index) => {
      const zone = zones[Math.floor(next() * zones.length) % zones.length];
      return {
        message,
        top: `${zone.topMin + next() * (zone.topMax - zone.topMin)}%`,
        left: `${zone.leftMin + next() * (zone.leftMax - zone.leftMin)}%`,
        rotation: `${-45 + next() * 90}deg`,
        opacity: 0.68 + next() * 0.22,
        scale: 0.92 + next() * 0.22,
        key: `${message}-${index}`,
      };
    });
  }, [card?.id, hintMessageSeed, safeHintMessages]);

  const handleBadgeClick = (e) => {
    e.stopPropagation();
    if (interactionDisabled || showsAnswerDecisionControls || flipInFlightRef.current) return;
    restartAutoRepeatIdleHint();
    if (onClearMessageButton) {
      onClearMessageButton();
    }
    if (suppressBadgeClickRef.current) {
      suppressBadgeClickRef.current = false;
      return;
    }

    if (isFlashing || isAutoRepeatBursting) {
      return;
    }

    if (onBadgeClick) {
      onBadgeClick();
    }
  };

  const cancelAutoRepeatCharge = () => {
    if (autoRepeatTimerRef.current) {
      clearTimeout(autoRepeatTimerRef.current);
      autoRepeatTimerRef.current = null;
    }
    autoRepeatPointerStartRef.current = null;
    setIsAutoRepeatCharging(false);
  };

  const handleBadgePointerDown = (event) => {
    event.stopPropagation();
    if (interactionDisabled || showsAnswerDecisionControls || flipInFlightRef.current) return;
    restartAutoRepeatIdleHint();
    if (onClearMessageButton) {
      onClearMessageButton();
    }
    if (!autoRepeatAvailable || !onAutoRepeat) return;

    cancelAutoRepeatCharge();
    autoRepeatPointerStartRef.current = {
      x: Number(event.clientX) || 0,
      y: Number(event.clientY) || 0,
    };
    setIsAutoRepeatCharging(true);
    suppressBadgeClickRef.current = false;
    autoRepeatTimerRef.current = setTimeout(() => {
      autoRepeatTimerRef.current = null;
      suppressBadgeClickRef.current = true;
      setIsAutoRepeatCharging(false);
      setIsAutoRepeatBursting(false);
      requestAnimationFrame(() => setIsAutoRepeatBursting(true));
      onAutoRepeat();
    }, autoRepeatHoldMs);
  };

  const handleBadgePointerCancel = (event) => {
    event.stopPropagation();
    cancelAutoRepeatCharge();
    restartAutoRepeatIdleHint();
  };

  const handleBadgePointerMove = (event) => {
    if (interactionDisabled) return;
    if (!autoRepeatPointerStartRef.current) return;

    const deltaX = Math.abs((Number(event.clientX) || 0) - autoRepeatPointerStartRef.current.x);
    const deltaY = Math.abs((Number(event.clientY) || 0) - autoRepeatPointerStartRef.current.y);
    if (deltaX > 12 || deltaY > 12) {
      event.stopPropagation();
      cancelAutoRepeatCharge();
      restartAutoRepeatIdleHint();
    }
  };

  const shouldShowIdleMotion =
    idleHintAvailable
    && !showsAnswerDecisionControls
    && isAutoRepeatIdleHintVisible
    && !isFlashing
    && !isAutoRepeatCharging
    && !isAutoRepeatBursting;
  const shouldShowAutoRepeatIdleHint = autoRepeatAvailable && shouldShowIdleMotion;
  // The score badge and tap pointer share one feedback clock and motion state.
  const practiceFeedbackClasses = [
    getCountColorClass(sessionPracticeCount),
    isFlashing ? 'badge-flash' : '',
    isAutoRepeatCharging ? 'auto-repeat-charging' : '',
    isAutoRepeatBursting ? 'auto-repeat-burst' : '',
  ].filter(Boolean).join(' ');
  const showPhaseGuidance = Boolean(guidancePhase)
    && (isAutoRepeatIdleHintVisible || showPersistentGuidance)
    && idleHintAvailable && isGuidancePageActive;
  const showGuidance = showPhaseGuidance && guidancePhase !== 'answer';
  const showControlCaptions = practiceGuidancePhase ? showGuidance : showBeginnerArrowLabels;
  const showAutoRepeatGuidance = showGuidance && autoRepeatAvailable;
  // Phase instructions share the beginner/idle guidance mode, including O/X.
  // Fluency trials retain their incumbent timer / Self Test / repetition labels.
  const guidancePurposeKey = { recall: 'recallPurpose', answer: 'answerPurpose', repeat: 'repeatPurpose' }[guidancePhase];
  const guidancePurpose = showPhaseGuidance && showPracticePhaseCaption && guidancePurposeKey
    ? t(`practiceUi.cardGuidance.${guidancePurposeKey}`) : null;
  const ordinaryPhaseCaption = showPracticePhaseCaption
    ? { recall: 'Think it through', repeat: 'Say it loud' }[guidancePhase] : null;
  const vocabularyCaption = vocabularyAnswerRevealed ? 'Make it stick' : 'Think it through';
  const renderTapGuidance = () => showGuidance && (
    <div className="flashcard-tap-guidance">
      <span className={`flashcard-tap-pointer ${practiceFeedbackClasses} ${shouldShowIdleMotion ? 'auto-repeat-idle-hint' : ''}`.trim()} aria-hidden="true">
        <Pointer size={30} strokeWidth={1.8} aria-hidden="true" />
      </span>
      <span>{t(`practiceUi.cardGuidance.${guidancePhase === 'recall' ? 'knowAnswer' : 'repeat'}`)}</span>
    </div>
  );

  const toggleMessageSelection = (message) => {
    setMessageSelection((current) => {
      if (current.includes(message)) {
        return current.filter((item) => item !== message);
      }
      if (current.length >= 2) return current;
      return [...current, message];
    });
  };

  const handleSaveMessageSelection = (event) => {
    event.stopPropagation();
    if (onSaveHintMessages) {
      onSaveHintMessages(messageSelection);
    }
    setIsMessageChooserOpen(false);
    setMessageSelection([]);
  };

  const handleCancelMessageSelection = (event) => {
    event.stopPropagation();
    setIsMessageChooserOpen(false);
    setMessageSelection([]);
    if (onClearMessageButton) {
      onClearMessageButton();
    }
  };

  const renderMessageChooser = () => {
    if (!isMessageChooserOpen) return null;

    return (
      <div className="card-message-chooser" role="dialog" aria-label="Choose user message" onClick={(event) => event.stopPropagation()}>
        <div className="card-message-options">
          {availableMessageOptions.map((message) => {
            const selected = messageSelection.includes(message);
            const disabled = !selected && messageSelection.length >= 2;
            return (
              <button
                type="button"
                key={message}
                className={`card-message-option ${selected ? 'is-selected' : ''}`.trim()}
                disabled={disabled}
                onClick={(event) => {
                  event.stopPropagation();
                  toggleMessageSelection(message);
                }}
              >
                {getCardMessageLabel(message)}
              </button>
            );
          })}
        </div>
        <div className="card-message-actions">
          <button type="button" onClick={handleSaveMessageSelection} disabled={messageSelection.length === 0}>
            Save
          </button>
          <button type="button" onClick={handleCancelMessageSelection}>
            Cancel
          </button>
        </div>
      </div>
    );
  };

  const renderScoreHud = (side) => isRecallMode ? null : (
    <div className="flashcard-score-hud">
      <button
        type="button"
        disabled={interactionDisabled || activeSide !== side || showsAnswerDecisionControls}
        aria-label="Repeat current card"
        className={[
          'session-count-badge',
          practiceFeedbackClasses,
          shouldShowAutoRepeatIdleHint ? 'auto-repeat-idle-hint' : '',
        ].filter(Boolean).join(' ')}
        onClick={handleBadgeClick}
        onPointerDown={handleBadgePointerDown}
        onPointerUp={handleBadgePointerCancel}
        onPointerMove={handleBadgePointerMove}
        onPointerLeave={handleBadgePointerCancel}
        onPointerCancel={handleBadgePointerCancel}
        onAnimationEnd={() => {
          setIsFlashing(false);
          setIsAutoRepeatBursting(false);
        }}
        title={
          autoRepeatAvailable
            ? (onAutoRepeat ? 'Click to earn points. Hold for auto repeat.' : 'Tap for auto repeat')
            : 'Click to earn points'
        }
        style={{ cursor: 'pointer' }}
      >
        +{sessionScoreGained} pt
        {showAutoRepeatGuidance && activeSide === side && <span className="flashcard-badge-guidance">{t('practiceUi.cardGuidance.autoRepeat')}</span>}
      </button>
      {hasScoreBonuses && visibleBonusEvent?.cardIdentity === cardIdentity && visibleBonusEvent?.eventId === scoreBonusEventId && (
        <PointBonusTip bonuses={scoreBonuses} eventId={scoreBonusEventId} />
      )}
    </div>
  );

  return (
    <div className="flashcard-container">
      <div
        ref={swipe.surfaceRef}
        className={`flashcard show-${activeSide} ${levelClass} ${typeClass} ${isEncounterMode ? 'flashcard-encounter' : ''} ${isVocabularyMode ? `flashcard-vocabulary ${reverseMode ? 'is-reverse' : ''}` : ''} ${showsAnswerDecisionControls ? 'flashcard-self-test' : ''} ${selfTestFeedback ? 'flashcard-has-self-test-feedback' : ''} ${isAutoRepeatBursting ? 'flashcard-auto-repeat-bursting' : ''} ${interactionDisabled ? 'is-interaction-disabled' : ''}`.trim()}
        role={isRecallMode ? 'group' : undefined}
        aria-label={isRecallMode ? (activeSide === 'back' ? 'Answer card' : 'Question card') : undefined}
        aria-busy={isRecallMode ? Boolean(flipState) : undefined}
        aria-disabled={interactionDisabled}
        onClick={handleCardClick}
        onClickCapture={(event) => {
          if (swipe.suppressClick(event)) {
            event.preventDefault();
            event.stopPropagation();
          }
        }}
        {...swipe.handlers}
      >
        <div className="gesture-hints">
          <button
            type="button"
            disabled={interactionDisabled || encounterControlsDisabled}
            className={`gesture-arrow arrow-left ${showControlCaptions && !showsAnswerDecisionControls ? 'gesture-arrow-with-label' : ''} ${showsMessageButton ? 'gesture-arrow-message' : ''} ${showsAnswerDecisionControls ? 'gesture-arrow-answer-no' : ''}`.trim()}
            onClick={(e) => handleArrowClick(e, 'left')}
            title={showsMessageButton ? 'Leave message' : horizontalLeftLabel}
            aria-label={showsMessageButton ? 'Leave message' : horizontalLeftLabel}
          >
            <span className="gesture-arrow-symbol">
              {showsAnswerDecisionControls ? 'X' : (showsMessageButton ? '\u{1F4AC}' : (
                <Undo2 size={28} strokeWidth={2.8} aria-hidden="true" />
              ))}
            </span>
            {showControlCaptions && !showsMessageButton && !showsAnswerDecisionControls && (
              <span className="gesture-arrow-label">{t('practiceUi.cardGuidance.flip')}</span>
            )}
          </button>
          {renderMessageChooser()}
          <button
            type="button"
            disabled={interactionDisabled || encounterControlsDisabled || (showsAnswerDecisionControls && answerOkDisabled)}
            className={`gesture-arrow arrow-right ${showControlCaptions && !showsAnswerDecisionControls ? 'gesture-arrow-with-label' : ''} ${showsAnswerDecisionControls ? 'gesture-arrow-answer-ok' : ''}`.trim()}
            onClick={(e) => handleArrowClick(e, 'right')}
            title={horizontalRightLabel}
            aria-label={horizontalRightLabel}
          >
            <span className="gesture-arrow-symbol">
              {showsAnswerDecisionControls ? 'O' : (
                <Redo2 size={28} strokeWidth={2.8} aria-hidden="true" />
              )}
            </span>
            {showControlCaptions && !showsAnswerDecisionControls && (
              <span className="gesture-arrow-label">{t('practiceUi.cardGuidance.flip')}</span>
            )}
          </button>
          {!hideVerticalNavigation && !showsAnswerDecisionControls && (
            <>
              {!isVocabularyMode && !hidePreviousNavigation && <button
                type="button"
                disabled={interactionDisabled || verticalNavigationDisabled}
                className={`gesture-arrow arrow-up ${showControlCaptions ? 'gesture-arrow-with-label' : ''} ${verticalNavigationDisabled ? 'gesture-arrow-disabled' : ''}`.trim()}
                onClick={(e) => handleVerticalArrowClick(e, 'previous')}
                title="Previous card"
                aria-label="Previous card"
                aria-disabled={verticalNavigationDisabled}
              >
                <span className="gesture-arrow-symbol">{'\u2191'}</span>
                {showControlCaptions && (
                  <span className="gesture-arrow-label">Prev</span>
                )}
              </button>}
              {!hideNextNavigation && <button
                type="button"
                disabled={interactionDisabled || verticalNavigationDisabled}
                className={`gesture-arrow arrow-down ${showControlCaptions ? 'gesture-arrow-with-label' : ''} ${verticalNavigationDisabled ? 'gesture-arrow-disabled' : ''}`.trim()}
                onClick={(e) => handleVerticalArrowClick(e, 'next')}
                title="Next card"
                aria-label="Next card"
                aria-disabled={verticalNavigationDisabled}
              >
                <span className="gesture-arrow-symbol">{'\u2193'}</span>
                {showControlCaptions && (
                  <span className="gesture-arrow-label">Next</span>
                )}
              </button>}
            </>
          )}
        </div>
        <div
          className={`flashcard-inner show-${activeSide} ${flipClass}`.trim()}
          onAnimationEnd={handleFlipAnimationEnd}
          style={{
            '--flashcard-rotation': `${flipState?.toRotation ?? rotationRef.current}deg`,
            '--flashcard-flip-from': `${flipState?.fromRotation ?? rotationRef.current}deg`,
            '--flashcard-flip-to': `${flipState?.toRotation ?? rotationRef.current}deg`,
            '--flashcard-flip-drag-from': `${flipState?.fromDragX ?? 0}px`,
            '--flashcard-flip-duration': `${flipState?.durationMs ?? 700}ms`,
          }}
        >
          <div className="flashcard-front" aria-hidden={activeSide !== 'front'}>
            {noteCoverUrl && (
              <div
                className="flashcard-note-cover flashcard-note-cover-front"
                style={{ backgroundImage: `url(${noteCoverUrl})` }}
                aria-hidden="true"
              />
            )}
            {showClearedStamp && (
              <img
                className={`flashcard-cleared-stamp flashcard-cleared-stamp-${graduationType}`}
                src={getGraduationStamp(graduationType)}
                alt={`${graduationType || 'manual'} graduation stamp`}
              />
            )}
            <div className={`flashcard-corner-caption flashcard-corner-caption-front ${guidancePurpose ? 'flashcard-corner-guidance' : ''} ${quickTestCaptionClass}`.trim()}>
              {showQuickTestCaption ? (
                <>
                  <span>{quickTestStatus.label || 'Grade 4 Test'}</span>
                  <strong>{quickTestStatus.value}</strong>
                </>
              ) : (
                guidancePurpose || (isVocabularyMode ? vocabularyCaption : isEncounterMode ? 'Q' : ordinaryPhaseCaption || 'Think it through')
              )}
            </div>
            {vocabularyOrdinalBadge}
            {vocabularyGradeBadge}
            {levelBadgeLabel && (
              <div className="flashcard-level-badge" title={`Level ${cardLevel}`}>
                {levelBadgeLabel}
              </div>
            )}
            {renderScoreHud('front')}
            {shouldRenderHintMessages && (
              <div className="flashcard-user-message-layer" aria-label="Saved user message">
                {hintLayout.map((item) => (
                  <span
                    key={item.key}
                    className="flashcard-user-message"
                    style={{
                      top: item.top,
                      left: item.left,
                      opacity: item.opacity,
                      transform: `translate(-50%, -50%) rotate(${item.rotation}) scale(${item.scale})`,
                    }}
                  >
                    {getCardMessageLabel(item.message)}
                  </span>
                ))}
              </div>
            )}
            <div className={`flashcard-content ${frontFontClass}`}>
              <MarkdownText text={frontFaceText} />
              {frontFaceMemo && (
                <div className="flashcard-side-memo">
                  <MarkdownText text={frontFaceMemo} />
                </div>
              )}
            </div>
            {renderTapGuidance()}
            {showControlCaptions && !showGuidance && (
              <div className="flashcard-hint">
                Click to practice
              </div>
            )}
          </div>
          <div className="flashcard-back" aria-hidden={activeSide !== 'back'}>
            {showsAnswerDecisionControls && <div className="flashcard-self-test-zones" aria-hidden="true" />}
            {noteCoverUrl && (
              <div
                className="flashcard-note-cover flashcard-note-cover-back"
                style={{ backgroundImage: `url(${noteCoverUrl})` }}
                aria-hidden="true"
              />
            )}
            {showClearedStamp && (
              <img
                className={`flashcard-cleared-stamp flashcard-cleared-stamp-${graduationType}`}
                src={getGraduationStamp(graduationType)}
                alt={`${graduationType || 'manual'} graduation stamp`}
              />
            )}
            <div className={`flashcard-corner-caption flashcard-corner-caption-back ${guidancePurpose ? 'flashcard-corner-guidance' : ''}`.trim()}>
              {guidancePurpose || (isVocabularyMode ? vocabularyCaption : isEncounterMode ? 'A' : ordinaryPhaseCaption || (showsAnswerDecisionControls ? 'Self Test' : 'Say it aloud'))}
            </div>
            {vocabularyOrdinalBadge}
            {vocabularyGradeBadge}
            {levelBadgeLabel && (
              <div className="flashcard-level-badge flashcard-level-badge-back" title={`Level ${cardLevel}`}>
                {levelBadgeLabel}
              </div>
            )}
            {renderScoreHud('back')}
            <div className={`flashcard-content ${backFontClass}`}>
              <MarkdownText text={backFaceText} />
              {backFaceMemo && (
                <div className="flashcard-side-memo flashcard-side-memo-back">
                  <MarkdownText text={backFaceMemo} />
                </div>
              )}
            </div>
            {renderTapGuidance()}
            {showControlCaptions && !showGuidance && !showsAnswerDecisionControls && (
              <div className="flashcard-hint">
                {backPracticeHint}
              </div>
            )}
          </div>
        </div>
        {!isRecallMode && <CardJudgmentFeedback result={selfTestFeedback} />}
        {!isRecallMode && cardType === PracticeSettingsService.CARD_TYPES.SIMPLE && (
          <span className="flashcard-size-badge" aria-label="Short card" title="Short card">
            S
          </span>
        )}
      </div>
    </div>
  );
}

export default FlashCard;
